<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <div
        v-clickoutside="{
            handler: preventHideOnClick ? handlePreventHideOnClick : closeDropdown,
            include: [dropdownRef] }
        "
        :class="[
            'el-select',
            'qa-select',
            translucent ? 'el-select--translucent' : ''
        ]"
        @keydown.enter.stop.prevent="handleEnterKey"
    >
        <el-input
            :id="id"
            ref="triggerRef"
            v-bind="$attrs"
            :model-value="computedLabel"
            :disabled="disabled"
            :name="name"
            :label="label"
            :readonly="true"
            :title="computedTitle"
            :placeholder="placeholder"
            :validate-event="false"
            :class="{
                'is-focus': visible,
                'qa-select-input': true
            }"
            :prefix-icon="placeholderIcon"
            :icon-color="placeholderIconColor"
            :aria-label="ariaLabel || label || placeholder"
            @click="!openOnIconClick && handleClick($event)"
            @focus="handleFocus"
            @blur="handleBlur"
            @input="changeValue"
            @keydown.down.stop.prevent="handleArrowKey('next')"
            @keydown.up.stop.prevent="handleArrowKey('prev')"
            @keydown.tab="closeDropdown({ blur: false })"
        >
            <template v-if="!!$slots.prefix" #selectPrefix>
                <slot name="prefix" />
            </template>
            <template v-if="!!$slots.selectSuffix" #selectSuffix>
                <slot name="selectSuffix" />
            </template>
            <template #suffix>
                <slot name="suffix" />
                <a-glyph
                    :name="iconClass"
                    fill="brand-primary"
                    :class="['el-select__caret', 'el-input__icon', 'qa-select-icon']"
                    @click="deleteSelected"
                />
            </template>
        </el-input>

        <teleport to="#teleports" :disabled="!shouldTeleport">
            <el-select-dropdown
                v-if="showDropdown || visible || selectDropdownMounted || !flyweight"
                ref="dropdownRef"
                :visible="showDropdown || visible"
                :filterable="filterable"
                :search-placeholder="searchPlaceholder"
                :placement="popperPlacement"
                :max-width="popperMaxWidth"
                :dynamic-width="dynamicWidth"
                :hide-on-resize="hideOnResize"
                @searchedit="onSearchEdit"
                @keydown.down.stop.prevent="handleArrowKey('next')"
                @keydown.up.stop.prevent="handleArrowKey('prev')"
                @keydown.esc.stop.prevent="closeDropdown({ blur: false })"
                @keydown.enter.stop.prevent="handleEnterKey"
                @keydown.tab="closeDropdown()"
            >
                <el-scrollbar
                    v-show="isOptionsSectionShown"
                    ref="scrollbar"
                    tag="ul"
                    role="listbox"
                    :aria-multiselectable="multiple || null"
                    :wrap-style="`max-height: ${popperMaxHeight};`"
                    wrap-class="el-select-dropdown__wrap"
                    view-class="el-select-dropdown__list"
                    :dropdown="true"
                    :class="{ 'is-empty': hasQuery && filteredOptionsCount === 0 }"
                    @scroll="handleDropdownScroll"
                >
                    <div
                        v-if="multiple && selectAll"
                        v-show="!hasQuery"
                    >
                        <el-option
                            :key="selectAllValue"
                            :value="selectAllValue"
                            :label="selectAllLabel"
                            :is-touch-scrolling="isTouchScrolling"
                            @click.stop.prevent="handleAllClick"
                        />
                        <el-divider />
                    </div>
                    <div v-if="isRemoteSelectedSectionShown">
                        <el-option
                            v-for="item in previousSelected"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                            :icon="item.icon"
                            :is-touch-scrolling="isTouchScrolling"
                            :icon-color="item.iconColor"
                            :force-visible="true"
                        />
                        <el-divider v-show="isDividerShown" />
                    </div>
                    <div
                        v-if="isRemoteSearchResultsCountShown"
                        class="el-select-dropdown__search-count"
                    >
                        {{ computedRemoteSearchResultsCount }}
                    </div>
                    <!-- @slot Default slot to render the contents to show inside the select input -->
                    <slot v-if="!isLoadingShown" />
                    <div
                        v-if="scrollLoading && !isLoadingShown"
                        class="el-select-dropdown__scroll-loading"
                    >
                        <el-spinner
                            color="brand-primary"
                            class="mr-8"
                        />
                    </div>
                    <div v-if="emptyText && !hasCount" class="el-select-dropdown__empty">
                        {{ emptyText }}
                    </div>
                </el-scrollbar>
                <div
                    v-if="isLoadingShown"
                    v-loading="isLoadingShown"
                    class="el-select__loading"
                    el-loading-size="24"
                />
            </el-select-dropdown>
        </teleport>
    </div>
</template>

<script setup>
import { isEqual } from 'lodash-es';
import { ref, computed, inject, watch, nextTick, provide, onMounted, onBeforeUnmount } from 'vue';
import ElDivider from '@core/components/divider/divider.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import ElInput from '@core/components/input/input.vue';
import { Loading } from '@core/components/loading';
import ElScrollbar from '@core/components/scrollbar/scrollbar.vue';
import ElSpinner from '@core/components/spinner/spinner.vue';
import { useDebouncedFn } from '@core/composables/useDebouncedFn';
import { useDropdownScroll } from '@core/composables/useDropdownScroll.ts';
import { useKeyboardNavigate } from '@core/composables/useKeyboardNavigate.ts';
import { usePopper } from '@core/composables/usePopper.ts';
import Clickoutside from '@core/directives/v-clickoutside';
import ElOption from './option.vue';
import ElSelectDropdown from './select-dropdown.vue';
import { valueEquals } from './utils.js';

const emit = defineEmits([
    'update:modelValue',
    'change',
    'focus',
    'input',
    'click',
    'blur',
    'visible-change',
    'remove-tag',
    'searchedit',
    'queryChange',
    'clear',
    'update:showDropdown',
]);

defineOptions({
    directives: {
        loading: Loading.directive,
        clickoutside: Clickoutside,
    },
});

const elFormItem = inject('elFormItem', null);

const props = defineProps({
    name: { type: String, default: '' },
    id: { type: String, default: '' },
    label: { type: String, default: '' },
    modelValue: {type: [String, Number, Boolean, Object, Array, null], required: true },
    size: { type: String, default: '' },
    disabled: Boolean,
    clearable: Boolean,
    customText: { type: String, default: '' },
    customHint: Boolean,
    filterable: { type: Boolean, default: false },
    loading: Boolean,
    scrollLoading: Boolean,
    isScrollEnd: {type: Boolean, default: true },
    popperClass: { type: String, default: '' },
    popperPlacement: { type: String, default: 'bottom-start' },
    openOnIconClick: Boolean,
    hideOnScroll: Boolean,
    hideOnResize: Boolean,
    dynamicWidth: Boolean,
    popperMaxWidth: { type: String, default: '' },
    popperMaxHeight: { type: String, default: '336px' },
    readonly: Boolean,
    remote: Boolean,
    validateEvent: { type: Boolean, default: true },
    loadingText: { type: String, default: 'Loading' },
    noMatchText: { type: String, default: 'No match' },
    noDataText: { type: String, default: 'No data' },
    remoteMethod: {type: Function, default: undefined},
    remoteScrollMethod: { type: Function, default: undefined },
    filterMethod: { type: Function, default: undefined },
    multiple: Boolean,
    multipleLimit: { type: Number, default: 0 },
    multipleShowItemsLimit: { type: Number, default: 3 },
    showDropdown: Boolean,
    allowCreate: Boolean,
    defaultFirstOption: Boolean,
    placeholder: { type: String, default: 'Select' },
    placeholderIcon: { type: String, default: '' },
    placeholderIconColor: { type: String, default: '' },
    searchPlaceholder: { type: String, default: 'Search' },
    selectAll: { type: [Boolean, String], default: false },
    allItemsSelectedText: { type: String, default: '' },
    reserveKeyword: Boolean,
    valueKey: { type: String, default: 'value' },
    flyweight: Boolean,
    changeOnOptionsRemoval: { type: Boolean, default: true },
    remoteSearchResultsCount: { type: [Number, String], default: undefined },
    showSelectedIcon: Boolean,
    translucent: Boolean,
    preventHideOnClick: Boolean,
    shouldTeleport: Boolean,
    ariaLabel: { type: String, default: '' },
});

/* Refs */
const triggerRef = ref(null);
const dropdownRef = ref(null);
const scrollbar = ref(null);
const options = ref([]);
const optionsCount = ref(0);
const cachedOptions = ref([]);
const filteredOptionsCount = ref(0);
const currentPlaceholder = ref('');
const cachedPlaceHolder = ref('');
const createdLabel = ref(null);
const createdSelected = ref(false);
const query = ref('');
const selectAllValue = ref('all');
const previousQuery = ref('');
const previousScrollTop = ref('');
const previousSelected = ref([]);
const searchUnchangedSinceOpen = ref(true);
const visible = ref(false);
const selectDropdownMounted = ref(false);
const selected = ref(props.multiple ? [] : {});
const selectedIcon = ref('');
const selectedIconColor = ref('');
const selectedLabel = ref('');
const isTouchScrolling = ref(false);

/* Composables */
const { initializePopper, updatePopper, destroyPopper } = usePopper(triggerRef, dropdownRef, {
    placement: props.popperPlacement,
    strategy: 'fixed',
    modifiers: [
        { name: 'offset', options: { offset: [0, 8] } },
        {
            name: 'flip',
            options: { padding: 8, fallbackPlacements: ['top-start'] },
        },
        {
            name: 'preventOverflow',
            options: {
                boundary: 'clippingParents',
                tether: false,
            },
        },
    ],
});
const {scrollToOption,
    setScrollPosition,
    updateHoverOption,
    setDefaultHoverIndex,
    receiveScrollTop} = useDropdownScroll(dropdownRef, 'el-select-dropdown__wrap');

const { debouncedFn: updateValueDeb } = useDebouncedFn(updateValue, 100);
const {debouncedFn: debouncedRemoteMethod,
    isDebouncing: isWithinRemoteDecouncePeriod} = useDebouncedFn(
    props.remoteMethod,
    500,
    {
        onDebounceEnd: () => {
            isWithinRemoteDecouncePeriod.value = false;
        },
        onDebounceStart: () => {
            isWithinRemoteDecouncePeriod.value = true;
        },
    },
);

const {hoverIndex,
    hoverOption,
    keyboardNavigate,
    navigateOptions,
    resetHoverState} = useKeyboardNavigate({
    options,
    visible,
    scrollToOption,
    selected,
    multiple: props.multiple,
});


/* Computed */
const selectAllLabel = computed(() => typeof props.selectAll === 'string' ? props.selectAll : 'All');
const computedLabel = computed(() => {
    if (props.customText && typeof props.customText === 'string') return props.customText;
    return props.multiple ? multipleText.value.text : selectedLabel.value;
});
const multipleText = computed(() => {
    const selectedList = selected.value ?? [];
    if (!selectedList.length) return '';

    const filtered = selectedList;
    const sorted = props.remote ? filtered : [...filtered].sort((a, b) => a._uid - b._uid); // eslint-disable-line no-underscore-dangle
    const count = sorted.length;
    const title = sorted.map(x => x.currentLabel).join(', ').trim();
    const allSelected = props.selectAll &&
    options.value.length - 1 === props.modelValue.length &&
    props.allItemsSelectedText;
    const text = allSelected
        ? props.allItemsSelectedText
        : count <= props.multipleShowItemsLimit
            ? title
            : `${count} items selected`;

    return { text, title };
});
const computedTitle = computed(() => {
    if (triggerRef.value?.type === 'password') return '';
    if (typeof props.customHint === 'string') return props.customHint;

    const hasLabel = props.multiple ? multipleText.value?.title?.length : selectedLabel.value?.length;
    return hasLabel ? (
        props.multiple ? multipleText.value.title : selectedLabel.value
    ) : props.placeholder;
});
const selectionCount = computed(() => {
    if (props.multiple) return selected.value.length;
    return selected.value ? 1 : 0;
});
const isDividerShown = computed(() =>
    typeof props.remoteSearchResultsCount === 'number' &&
    previousSelected.value?.length &&
    props.remoteSearchResultsCount > previousSelected.value.length,
);
const isClearable = computed(() =>  props.clearable && !props.disabled && selectionCount.value);
const iconClass = computed(() => isClearable.value ? 'times' : (visible.value ? 'i-chevron-up--16' : 'i-chevron-down--16'));
const computedRemoteSearchResultsCount = computed(() => {
    return typeof props.remoteSearchResultsCount === 'number'
        ? `${props.remoteSearchResultsCount} items found`
        : props.remoteSearchResultsCount;
});
const isLoadingShown = computed(() => props.loading
    ? props.loading
    : (props.remote && props.remoteMethod && isWithinRemoteDecouncePeriod.value));

const isRemoteFilterableMultiple = computed(() => {
    return props.remote && props.filterable && props.multiple;
});

const isRemoteSelectedSectionShown = computed(() => {
    return isRemoteFilterableMultiple.value &&
          !isLoadingShown.value && previousSelected.value.length && !query.value.length;
});
const hasResults = computed(() => options.value.length);
const hasQuery = computed(() => Boolean(query.value?.trim()));
const hasCount = computed(() => Boolean(computedRemoteSearchResultsCount.value));
const isRemoteSearchResultsCountShown = computed(() =>
    props.remote &&
  props.filterable &&
  hasCount.value &&
  hasQuery.value &&
  hasResults.value &&
  !isLoadingShown.value,
);

const emptyText = computed(() => {
    if (isLoadingShown.value) return '';
    if (!filteredOptionsCount.value) {
        return hasQuery.value ? props.noMatchText : props.noDataText;
    }
    return '';
});

const isOptionsSectionShown = computed(() => !isLoadingShown.value && (hasResults.value || Boolean(emptyText.value)));

/* Methods */
let touchScrollTimer = null;
let wrapEl = null;

function markTouchScrolling() {
    isTouchScrolling.value = true;
    clearTimeout(touchScrollTimer);
    touchScrollTimer = setTimeout(() => {
        isTouchScrolling.value = false;
    }, 60);
}

function bindWrapListeners() {
    // get the fresh wrap every time the dropdown appears
    wrapEl = dropdownRef.value?.$el?.querySelector('.el-select-dropdown__wrap');
    if (!wrapEl) return;
    wrapEl.addEventListener('touchmove', markTouchScrolling, { passive: true });
    wrapEl.addEventListener('touchcancel', markTouchScrolling, { passive: true });
    wrapEl.addEventListener('pointercancel', markTouchScrolling, { passive: true });
}

function unbindWrapListeners() {
    if (!wrapEl) return;
    wrapEl.removeEventListener('touchmove', markTouchScrolling);
    wrapEl.removeEventListener('touchcancel', markTouchScrolling);
    wrapEl.removeEventListener('pointercancel', markTouchScrolling);
    wrapEl = null;
}

function updatePlaceholder(val) {
    currentPlaceholder.value = val.length > 0 ? '' : cachedPlaceHolder.value;
}

function updateValue(val) {
    if (props.multiple) {
        updatePlaceholder(val);
        if (props.selectAll && options.value.length) {
            syncSelectAllOption(options.value);
        }
    } else {
        resetHoverState();
    }

    setSelected();
};

function setSelected() {
    cacheDropdownScroll();

    if (props.multiple) {
        updateMultipleSelected();
    } else {
        updateSingleSelected();
    }
}

function changeValue(val) {
    if (!valueEquals(props.modelValue, val)) {
        emit('update:modelValue', val);
        emit('input', val);
        emit('change', val);

        if (props.validateEvent) {
            elFormItem?.validate?.('change');
            elFormItem?.markAsTouched?.();
        }
    }
};

function getOption(val) {
    return options.value.find(opt => isEqual(opt.value, val)) ||
        cachedOptions.value.find(opt => isEqual(opt.value, val)) ||
        null;
}

function updateSingleSelected() {
    const option = getOption(props.modelValue);
    selected.value = option || null;

    if (!option) {
        selectedLabel.value = '';
        selectedIcon.value = '';
        selectedIconColor.value = '';
        return;
    }

    selectedLabel.value = option.label;
    createdSelected.value = Boolean(option.created);
    createdLabel.value = option.created ? option.currentLabel : null;

    if (props.showSelectedIcon && option.icon) {
        selectedIcon.value = option.icon;
        selectedIconColor.value = option.iconColor;
    } else {
        selectedIcon.value = '';
        selectedIconColor.value = '';
    }
}

function updateMultipleSelected() {
    selected.value = [];
    if (!Array.isArray(props.modelValue)) return;

    props.modelValue.forEach((value) => {
        selected.value.push(getOption(value));
    });
}

function cacheDropdownScroll() {
    const dropdown = dropdownRef.value?.$el?.querySelector('.el-select-dropdown__wrap');
    if (!dropdown) return;
    previousScrollTop.value = dropdown.scrollTop;
}

function onSearchEdit(newValue) {
    emit('searchedit', newValue);
    query.value = newValue;
    handleQueryChange(query.value);
    if (hasQuery.value) return;
    previousSelected.value = selected.value;
};

async function handleQueryChange(val, forceQuery = false) {
    if (previousQuery.value === val && !forceQuery) return;
    previousQuery.value = val;
    hoverIndex.value = -1;

    if (props.remote && typeof props.remoteMethod === 'function') {
        hoverIndex.value = -1;
        scrollbar.value?.$refs?.wrap?.scrollTo?.(0, 0);
        debouncedRemoteMethod(val);
    } else if (typeof props.filterMethod === 'function') {
        props.filterMethod(val);
        emit('queryChange');
    } else {
        emit('queryChange', val);
        await nextTick();
        filteredOptionsCount.value = options.value.filter(opt => {
            try {
                return opt.visible;
            } catch {
                return true;
            }
        }).length;
    }

    await nextTick();
    updatePopper();
    maybeCheckDefaultFirstOption();
    if (!props.remote) {
        options.value.forEach(option => {
            // eslint-disable-next-line no-param-reassign
            option.keyword = val;
        });
    }
}

function handleEnterKey() {
    if (visible.value) {
        selectOption();
    } else {
        openDropdown();
    }
}

function handleArrowKey(direction) {
    if (!visible.value && !props.disabled) {
        openDropdown();
        nextTick(() => navigateOptions(direction));
    } else {
        navigateOptions(direction);
    }
}

function handleDropdownEnter() {
    nextTick(() => {
        requestAnimationFrame(() => {
            if (!query.value && selected.value) {
                const option = props.multiple ? selected.value?.[0] : selected.value;
                const index = options.value.findIndex(opt => opt === option);
                if (index >= 0) {
                    updateHoverOption(index, options.value, hoverOption);
                    hoverIndex.value = index;
                    scrollToOption(option);
                    return;
                }
            }
            const fallbackOption = props.multiple ? selected.value?.[0] : selected.value;
            if (fallbackOption) {
                scrollToOption(fallbackOption);
            }
        });
    });
}

function handlePreventHideOnClick() {
    if (props.showDropdown || !visible.value) return;
    visible.value = true;
    receiveScrollTop();
};

function handleFocus(event) {
    emit('focus', event);
    elFormItem?.markAsFocused?.();
    elFormItem?.markAsTouched?.();
};

function handleBlur(event) {
    if (!visible.value) {
        emit('blur', event);
        if (props.validateEvent) {
            elFormItem?.validate?.('blur');
        }
        elFormItem?.markAsBlurred?.();
        nextTick(() => highlightLabel(query.value));
    }
};

function highlightLabel(value) {
    if (value !== '' && !props.remote) {
        options.value.forEach((option) => {
            // eslint-disable-next-line no-param-reassign
            option.keyword = value;
        });
    }
};

function handleClick(event) {
    toggleDropdown();
    emit('click', event);
};

function handleOptionClick(option) {
    if (isTouchScrolling.value) return;

    handleOptionSelect(option);

    if (!props.multiple) {
        closeDropdown({ preserveHover: false });
    }
};

function handleDropdownScroll() {
    markTouchScrolling();

    if (props.remote && typeof props.remoteScrollMethod === 'function' &&
          !isWithinRemoteDecouncePeriod.value && !props.scrollLoading && !props.isScrollEnd) {
        const scrollWrapEl = dropdownRef.value.$el.querySelector('.el-select-dropdown__wrap');
        const { scrollTop, clientHeight, scrollHeight } = scrollWrapEl;
        if (scrollHeight - scrollTop - clientHeight <= 15 && !props.isScrollEnd) {
            props.remoteScrollMethod(query.value);
        }
    }
};

function getValueIndex(arr = [], value) {
    const isObject = Object.prototype.toString.call(value).toLowerCase() === '[object object]';
    return isObject
        ? arr.findIndex(item => isEqual(item, value))
        : arr.indexOf(value);
};

function handleAllClick() {
    if (!options.value[0].isSelected) {
        const newValue = options.value.slice(1).map((option) => option[props.valueKey]);

        changeValue(newValue);
        syncSelectAllOption(options.value);
        updateMultipleSelected();
    } else {
        changeValue([]);
        syncSelectAllOption(false);
        updateMultipleSelected();
    }
};

function deleteSelected(event) {
    if (!isClearable.value) return;

    const initialValue = props.multiple ? [] : '';
    event.stopPropagation();
    changeValue(initialValue);
    emit('clear');
    resetHoverState();
    if (props.multiple) updateMultipleSelected();
}

function handleRemoteScrollReset() {
    if (!props.remote) return;
    nextTick(() => {
        scrollbar.value?.updateScrollPosition?.();
    });
}

function maybeCheckDefaultFirstOption() {
    if (
        props.defaultFirstOption &&
    (props.filterable || props.remote) &&
    filteredOptionsCount.value
    ) {
        setDefaultHoverIndex(hoverIndex, options.value, query.value);
    }
}

function selectOption() {
    if (!visible.value || isTouchScrolling.value) return;

    const isSingle = !props.multiple;

    let option = options.value[hoverIndex.value];

    if (!option && isSingle && selected.value) {
        option = selected.value;
    }

    if (!option || !option.visible || option.disabled || option.groupDisabled) return;

    handleOptionSelect(option);

    if (isSingle) {
        closeDropdown({ preserveHover: false });
    }
}

function handleOptionSelect(option) {
    if (props.selectAll && option === options.value[0]) return;

    if (props.multiple) {
        const value = props.modelValue.slice();
        const idx = getValueIndex(value, option.value);
        if (idx > -1) value.splice(idx, 1);
        else if (props.multipleLimit <= 0 || value.length < props.multipleLimit) value.push(option.value);

        changeValue(value);
        if (option.created) {
            query.value = '';
            handleQueryChange('');
        }
    } else {
        keyboardNavigate.value = false;
        changeValue(option.value);
    }

    if (props.showSelectedIcon && option.icon) {
        selectedIcon.value = option.icon;
        selectedIconColor.value = option.iconColor;
    }

    nextTick(() => scrollToOption(option));
    elFormItem?.markAsFocused?.();
}

function syncSelectAllOption(val) {
    if (!props.selectAll || !val.length) return;

    const isAllSelected = val.length - 1 === props.modelValue.length;
    // eslint-disable-next-line no-param-reassign
    val[0].isSelected = isAllSelected;
}

function registerOption(option) {
    cacheDropdownScroll();
    if (!cachedOptions.value.some(opt => isEqual(opt.value, option.value))) {
        cachedOptions.value.push(option);
    }
    options.value.push(option);
    optionsCount.value++;
    filteredOptionsCount.value++;
    setSelected();
}

function unregisterOption(option) {
    cacheDropdownScroll();
    const index = options.value.indexOf(option);
    if (index !== -1) {
        optionsCount.value--;
        filteredOptionsCount.value--;
        options.value.splice(index, 1);
    }
}

function handleOptionsRemoval(val) {
    if (!props.changeOnOptionsRemoval || props.remote) return;

    if (props.multiple) {
        const newValue = val
            .filter(option => props.modelValue.some(el => isEqual(el, option.value)))
            .map(option => option.value);

        changeValue(newValue);
    } else {
        const stillExists = val.some(option => isEqual(props.modelValue, option.value));
        if (!stillExists) {
            changeValue('');
            emit('clear');
        }
    }
}

function toggleDropdown() {
    if (visible.value || props.showDropdown) {
        receiveScrollTop();
        setScrollPosition();
    }

    if (!props.disabled) {
        if (visible.value) {
            closeDropdown({ preserveHover: false });
        } else {
            openDropdown();
        }
    }
}

function openDropdown() {
    if (props.disabled || visible.value) return;
    visible.value = true;
    emit('update:showDropdown', true);


    if (props.filterable) {
        handleQueryChange(query.value, true);
        if (props.remote) {
            searchUnchangedSinceOpen.value = true;
            previousSelected.value = selected.value;
        }
    }

    nextTick(async () => {
        resetHoverState();
        maybeCheckDefaultFirstOption();
        await initializePopper();
        requestAnimationFrame(() => {
            handleDropdownEnter();
            updatePopper();
            bindWrapListeners();
            dropdownRef.value?.$el?.querySelector('.el-input__editor')?.focus?.({ preventScroll: true });
        });
    });

    emit('visible-change', true);
}

/**
 * Closes the dropdown.
 * @param {Object} options
 * @param {boolean} options.blur - Whether to trigger blur events.
 * @param {boolean} options.preserveHover - Keep the current hover state (used in Enter key handling).
 */
function closeDropdown({ blur = true, preserveHover = false } = {}) {
    if (props.showDropdown || !visible.value) return;

    unbindWrapListeners();

    requestAnimationFrame(() => {
        visible.value = false;
        keyboardNavigate.value = false;
        emit('update:showDropdown', false);

        query.value = '';
        selectedLabel.value = '';

        if (props.filterable) {
            handleQueryChange(query.value, false);
        }

        if (!props.multiple && selected.value) {
            selectedLabel.value =
      props.filterable && createdSelected.value
          ? createdLabel.value
          : selected.value.currentLabel;
        }

        nextTick(() => {
            cacheDropdownScroll();
            triggerRef.value?.$el?.querySelector('.el-input__editor')?.focus?.({ preventScroll: true });
            if (!preserveHover) resetHoverState();
        });
    });

    if (blur) handleBlur();

    emit('visible-change', false);
}

watch([options, () => props.modelValue], () => {
    setSelected();
});

watch(() => props.placeholder, (val) => {
    cachedPlaceHolder.value = currentPlaceholder.value || val;
});

watch(() => props.placeholderIcon, (val) => {
    selectedIcon.value = val;
});

watch(() => props.showDropdown, (val) => {
    if (val && !visible.value) {
        openDropdown();
    } else if (!val && visible.value) {
        unbindWrapListeners();
        closeDropdown({ blur: false });
    }
});


watch(() => props.modelValue, (val) => {
    if (props.remote && props.filterable) {
        updateValueDeb(val);
    } else {
        updateValue(val);
    }
});

watch(() => hoverIndex.value, () => {
    updateHoverOption(hoverIndex.value, options.value, hoverOption);
});

watch(() => options.value, (val) => {
    handleOptionsRemoval(val);
    handleRemoteScrollReset();
    maybeCheckDefaultFirstOption();
    syncSelectAllOption(val);
    setSelected();
});

onMounted(() => {
    updatePlaceholder(currentPlaceholder.value || props.placeholder);
    if (props.multiple && !Array.isArray(props.modelValue)) {
        emit('update:modelValue', []);
    }

    nextTick(async () => {
        await initializePopper();
        updatePopper();
    });

    if (props.multiple && Array.isArray(props.modelValue) && props.modelValue.length > 0) {
        updatePlaceholder('');
    }

    if (props.remote && typeof props.remoteMethod === 'function') {
        debouncedRemoteMethod();
    }
    setSelected();
    resetHoverState();
});

onBeforeUnmount(() => {
    destroyPopper();
});

provide('select', {
    props,
    selected,
    query,
    triggerRef,
    dropdownRef,
    selectDropdownMounted,
    registerOption,
    unregisterOption,
    handleOptionClick,
    updateValue,
    setSelected,
    keyboardNavigate,
    previousSelected,
    options,
    cachedOptions,
    optionsCount,
    filteredOptionsCount,
    updatePopper,
    destroyPopper,
});

defineExpose({
    triggerRef,
    dropdownRef,
});
</script>

<style scoped lang="postcss">
.el-select {
    display: flex;
    cursor: pointer;
    position: relative;
    :deep(.el-input__container) {
        cursor: pointer;
        .el-input__editor {
            width: 100%;
        }
    }
}

.el-select-dropdown {
    @mixin paragraph;
    position: absolute;
    z-index: 2100;
    width: auto;
    max-width: 100%;
    font-family: var(--font-family-default);
    background: var(--av-inversed-primary);
    border: 1px solid var(--av-brand-primary);
    box-shadow: var(--av-shadow-regular);
    color: var(--av-fixed-primary);
    box-sizing: border-box;
    border-radius: 4px;
    margin: 8px 0;

    &__wrap {
        overflow-y: auto;
        touch-action: pan-y;
    }

    &__empty {
        @mixin body;
        line-height: 32px;
        color: var(--av-fixed-light);
        text-align: center;
        padding: 16px 24px;
    }

    &__scroll-loading {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 16px;
        position: relative;
        height: 40px;
    }

    &__search-count {
        @mixin caption;
        color: var(--av-fixed-light);
        padding: 0 16px;
        position: relative;
        height: 40px;
        box-sizing: border-box;
        text-align: left;
    }

    :deep(.el-input) {
        &__container {
            border: none;
        }
        &__wrapper {
            padding-top: 0;
        }
        &__editor {
            @mixin body;
            color: var(--av-fixed-primary);
        }
    }

    :deep(.el-select-dropdown__list) {
        list-style: none;
        padding: 8px 0;
        margin: 0;
        &.is-empty {
            padding: 0;
        }
    }

    :deep(.el-divider) {
        margin: 0;
    }
}
</style>
